"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Search, Scale, ShieldCheck, FileText, MessageSquare, CircleCheck as CheckCircle } from 'lucide-react';
export default function Methodology() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: listRef,
    isVisible: listVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const standards = [{
    icon: Search,
    title: "Market Research",
    summary: "We analyze real-time pricing data to establish your leverage.",
    detail: "Our team conducts deep-dive research into current market trends, dealer incentives, and regional pricing variations. We identify the true value of your target purchase, ensuring you never overpay based on outdated or inflated sticker prices.",
    badges: ["Data-Driven", "Market Analysis", "Price Benchmarking"]
  }, {
    icon: Scale,
    title: "Strategic Negotiation",
    summary: "Professional advocacy to secure the best possible terms.",
    detail: "We step in as your dedicated representative, handling all communications with sellers or dealerships. By removing the emotional pressure from the transaction, we maintain a firm, professional stance that protects your financial interests.",
    badges: ["Expert Advocacy", "Conflict Resolution", "Firm Stance"]
  }, {
    icon: ShieldCheck,
    title: "Contract Review",
    summary: "Protecting you from hidden fees and unfavorable clauses.",
    detail: "Before you sign anything, we meticulously review every line of the purchase agreement. We hunt for hidden administrative fees, predatory financing terms, and unnecessary add-ons that often inflate the final cost of large purchases.",
    badges: ["Fee Audit", "Contract Safety", "Transparency"]
  }, {
    icon: FileText,
    title: "Documentation",
    summary: "Ensuring all agreements are legally sound and clear.",
    detail: "We manage the paperwork trail, ensuring that every verbal agreement is captured in writing and that all terms are clearly defined. We provide you with a comprehensive summary of your purchase, giving you peace of mind.",
    badges: ["Paperwork Management", "Legal Clarity", "Record Keeping"]
  }, {
    icon: MessageSquare,
    title: "Direct Communication",
    summary: "We handle the back-and-forth so you don't have to.",
    detail: "Avoid the high-pressure sales tactics and endless follow-up calls. We act as the primary point of contact, filtering out the noise and only bringing you the final, optimized offers that meet our strict criteria for success.",
    badges: ["Stress-Free", "Direct Liaison", "Time Saving"]
  }, {
    icon: CheckCircle,
    title: "Final Verification",
    summary: "Confirming every detail before the transaction closes.",
    detail: "Our final step is a rigorous verification process. We confirm that the final invoice matches the negotiated terms exactly, ensuring that no last-minute changes or errors impact your bottom line.",
    badges: ["Quality Assurance", "Final Audit", "Success Guaranteed"]
  }];
  return <section data-section-id="4427" id="our-proven-method" className="bg-slate-50 text-gray-900 relative overflow-hidden py-32 md:py-48 lg:py-52">
      <div aria-hidden="true" className="pointer-events-none absolute -top-32 -right-32 h-[480px] w-[480px] rounded-lg opacity-20" style={{
      background: "radial-gradient(circle, #1a3a6b 0%, transparent 70%)",
      filter: "blur(60px)"
    }} />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-4">
            <div ref={headerRef} className={`lg:sticky lg:top-32 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <p className="font-default font-bold text-xs tracking-[0.3em] uppercase mb-8 text-[#e87a27]">
                Our Proven Method
              </p>
              <h2 className="font-default font-bold text-4xl md:text-5xl lg:text-6xl leading-[1.1] tracking-tighter text-[#1a3a6b] mb-8 uppercase">
                Advocacy that delivers results.
              </h2>
              <p className="text-base md:text-xl leading-relaxed font-default text-gray-700 max-w-md">
                We turn the tables on complex negotiations. Our structured approach ensures you have the data, the strategy, and the professional support needed to secure the best price on your next major purchase.
              </p>

              <div className="mt-10 hidden lg:block">
                <div className="h-px w-16 mb-6 bg-[#1a3a6b]" />
                <p className="font-default text-xs tracking-widest uppercase text-gray-500">
                  Six Steps to Savings
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-8">
            <div ref={listRef} className={`transition-all duration-1000 ease-out ${listVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <Accordion type="single" collapsible defaultValue="item-0" className="w-full space-y-6">
                {standards.map((standard, i) => {
                const Icon = standard.icon;
                return <AccordionItem key={i} value={`item-${i}`} data-index={i} className="group relative rounded-lg border border-gray-200 bg-white shadow-sm transition-all duration-500 hover:shadow-md data-[state=open]:border-[#1a3a6b] overflow-hidden">
                      <span aria-hidden="true" className="absolute left-0 top-0 h-full w-1 opacity-0 transition-opacity duration-500 group-data-[state=open]:opacity-100 bg-[#e87a27]" />

                      <AccordionTrigger className="px-6 md:px-8 py-6 md:py-7 hover:no-underline [&>svg]:text-[#1a3a6b] [&>svg]:size-6">
                        <div className="flex items-start gap-5 md:gap-6 text-left flex-1">
                          <Icon className="h-8 w-8 mt-1 shrink-0 text-[#e87a27]" />
                          <div className="flex-1 min-w-0">
                            <h3 className="font-default font-bold text-2xl md:text-[26px] leading-tight text-[#1a3a6b] mb-2 uppercase">
                              {standard.title}
                            </h3>
                            <p className="text-sm md:text-base font-default leading-relaxed text-gray-600">
                              {standard.summary}
                            </p>
                          </div>
                        </div>
                      </AccordionTrigger>

                      <AccordionContent className="px-6 md:px-8 pb-8">
                        <div className="pl-0 md:pl-14">
                          <div className="h-px w-12 mb-6 bg-[#e87a27]" />
                          <p className="text-base font-default leading-[1.75] text-gray-700 mb-6">
                            {standard.detail}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {standard.badges.map((badge, j) => <Badge key={j} data-index={j} variant="outline" className="rounded-md px-3 py-1 font-default text-[10px] tracking-widest uppercase border-[#1a3a6b]/20 text-[#1a3a6b] bg-[#1a3a6b]/5">
                                {badge}
                              </Badge>)}
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>;
              })}
              </Accordion>
            </div>
          </div>
        </div>
      </div>
    </section>;
}